/**
 * PO Excel 업로드(Style + Color) 화면 로직. #WK-20260821-05
 *
 * 컴포넌트에서 떼어낸 순수 함수라 5,003행 같은 대용량 fixture 로 단위 테스트할 수 있다.
 * 검증의 신뢰 경계는 서버이고 여기의 검증은 왕복을 줄이기 위한 선반영이다.
 */

/** 한 종류의 오류를 알림창에 몇 건까지 예시로 보여줄지. 나머지는 개수로만 알린다. */
export const MAX_MESSAGE_EXAMPLES = 20;

/** 상태별 안내 문구. 서버 {@code PurchaseExcelRowStatus} 와 짝을 이룬다. */
const STATUS_LABELS = {
	NOT_FOUND: 'Not found',
	AMBIGUOUS: 'Ambiguous (matched multiple items, skipped)',
	INELIGIBLE: 'Not orderable',
	INVALID: 'Invalid row',
};

const REASON_LABELS = {
	empty_row: 'empty row',
	class_code_missing: 'Style is empty',
	color_code_missing: 'Color is empty',
	class_code_too_long: 'Style is too long',
	color_code_too_long: 'Color is too long',
	unsupported_charset: 'unsupported characters',
	quantity_missing: 'Quantity is empty',
	quantity_not_integer: 'Quantity is not a whole number',
	quantity_not_positive: 'Quantity must be greater than 0',
	quantity_too_large: 'Quantity is too large',
	no_live_item: 'no matching item',
	multiple_live_items: 'more than one matching item',
	item_inactive: 'Inactive',
	class_inactive: 'Class inactive',
	class_closed: 'Class closed',
	class_closed_unknown: 'Class closed flag missing',
	class_missing: 'Class missing',
	blocked: 'Blocked',
};

/**
 * Excel 원문이 그대로 알림창 HTML 로 들어가지 않게 한다.
 * 코드 값에 꺾쇠나 따옴표가 있어도 마크업으로 해석되지 않는다.
 */
export function escapeHtml(value) {
	if (value === null || value === undefined) return '';
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function trimCell(value) {
	if (value === null || value === undefined) return '';
	// Excel 은 NBSP(U+00A0)·BOM(U+FEFF)을 자주 남기는데 String.trim 은 BOM 을 지우지 않는다.
	return String(value)
		.replace(/^[\s\u00a0\ufeff]+/, '')
		.replace(/[\s\u00a0\ufeff]+$/, '');
}

/**
 * 시트의 used-range 가 시작하는 물리 Excel 행 번호(1-base)를 돌려준다.
 *
 * SheetJS 는 `!ref`(xlsx 의 <dimension>)를 그대로 신뢰하므로, 위쪽에 여백이 있는 파일은
 * `!ref` 가 `A5:C6` 처럼 잡히고 `sheet_to_json({header:1})` 의 배열 index 0 이 물리 5행이 된다.
 * (<dimension> 태그가 없어도 SheetJS 가 실제 셀에서 추정해 같은 결과가 된다.)
 * 그대로 두면 오류 안내가 "excel row 1" 이라고 하는데 사용자 화면에서는 5행인 상황이 생긴다.
 *
 * AOA 자체를 A1 부터 강제로 읽게 바꾸면 JC 경로의 "1행 = 머리글" 스킵이 깨지므로,
 * 배열은 건드리지 않고 시작 행 번호만 보정한다.
 */
export function firstExcelRowNo(sheet, XLSX) {
	if (!sheet || !sheet['!ref'] || !XLSX || !XLSX.utils || !XLSX.utils.decode_range) return 1;
	try {
		// decode_range 는 잘못된 문자열에 예외를 던지지 않고 음수 좌표를 돌려준다.
		// 값이 정상 행 번호가 아니면 조용히 0 이나 음수를 쓰지 말고 1행 기준으로 되돌아간다.
		const first = XLSX.utils.decode_range(sheet['!ref']).s.r + 1;
		return Number.isInteger(first) && first > 0 ? first : 1;
	} catch (e) {
		return 1;
	}
}

/**
 * `sheet_to_json({ header: 1 })` 결과를 서버로 보낼 행으로 바꾼다.
 *
 * - `excel_row_no` 는 사용자가 Excel 에서 보는 물리 행 번호다. 배열 index 에
 *   used-range 시작 행(`first_row_no`)을 더해 구하며, 빈 행을 걸러내도 번호를 다시 매기지 않는다.
 *   번호를 다시 매기면 오류 안내가 엉뚱한 행을 가리킨다.
 * - 세 칸이 모두 빈 행은 보내지 않는다. 서식만 있고 값이 없는 파일은 used-range 가
 *   수천 행으로 잡히는데, 그걸 그대로 보내면 상한에 걸려 정상 파일까지 거절된다.
 *
 * @param {Array<Array>} rows sheet_to_json({header:1}) 결과
 * @param {number} first_row_no rows[0] 의 물리 Excel 행 번호(1-base). 기본 1
 */
export function parseExcelRowsForPurchase(rows, first_row_no) {
	const excel_rows = [];
	if (!rows || !rows.length) return excel_rows;

	const origin = first_row_no > 0 ? first_row_no : 1;

	for (let i = 0; i < rows.length; i++) {
		const row = rows[i] || [];
		const class_code = trimCell(row[0]);
		const color_code = trimCell(row[1]);
		const quantity = trimCell(row[2]);

		if (!class_code && !color_code && !quantity) continue;

		excel_rows.push({
			excel_row_no: origin + i,
			excel_class_code: class_code,
			excel_color_code: color_code,
			excel_quantity: quantity,
		});
	}
	return excel_rows;
}

/**
 * 기존 PO 품목에 업로드 품목을 합친다. 기존 구현은 새 품목마다 기존 배열 전체를
 * `filter` 해서 O(n*m) 이었고 5,003행에서 화면이 멈췄다. 여기서는 item_id 색인을
 * 한 번만 만들고 O(n) 으로 합친다.
 *
 * 기존 행은 수량만 더하고 `po_item_no`·`shipped_quantity` 등 저장 상태는 건드리지 않는다.
 *
 * @returns {{ items: Array, duplicated_labels: Array<string> }} 새 배열과 중복 품목 라벨
 */
export function mergeExcelItems(existing_items, new_items) {
	const merged = (existing_items || []).slice();
	const duplicated_labels = [];

	const index_by_item_id = new Map();
	for (let i = 0; i < merged.length; i++) {
		const item_id = merged[i] ? merged[i].item_id : null;
		if (item_id !== null && item_id !== undefined && !index_by_item_id.has(item_id)) {
			index_by_item_id.set(item_id, i);
		}
	}

	(new_items || []).forEach(item => {
		const index = index_by_item_id.get(item.item_id);
		if (index === undefined) {
			index_by_item_id.set(item.item_id, merged.length);
			merged.push(item);
			return;
		}
		// 수량만 더한다. amount·fob_amount·remain_* 는 items watcher 가 부르는 calTotal 이
		// JC 의 carton 배수까지 반영해 다시 계산하므로 여기서 중복 계산하지 않는다.
		const existing = merged[index];
		existing.quantity = (existing.quantity || 0) + (item.quantity || 0);
		duplicated_labels.push(item.item_code ? item.item_code : item.name + '(' + item.color_code + ')');
	});

	return { items: merged, duplicated_labels };
}

function describeRow(row) {
	const reason = REASON_LABELS[row.reason] || row.reason || '';
	let text = 'excel row ' + row.excel_row_no;
	if (row.excel_class_code || row.excel_color_code) {
		text += ', ' + escapeHtml(row.excel_class_code) + ' / ' + escapeHtml(row.excel_color_code);
	}
	if (reason) text += ' - ' + escapeHtml(reason);
	return text;
}

function appendGroup(lines, status, rows) {
	const group = rows.filter(r => r.status === status);
	if (!group.length) return;

	lines.push('');
	lines.push('<b>' + STATUS_LABELS[status] + '</b> : ' + group.length);
	group.slice(0, MAX_MESSAGE_EXAMPLES).forEach(r => lines.push(describeRow(r)));
	if (group.length > MAX_MESSAGE_EXAMPLES) {
		lines.push('... and ' + (group.length - MAX_MESSAGE_EXAMPLES) + ' more');
	}
}

/**
 * 업로드 결과 알림 본문(HTML). 수천 건이 와도 개수 + 제한된 예시만 만들어
 * 알림창이 브라우저를 멈추게 하지 않는다. Excel 유래 문자열은 모두 escape 한다.
 */
export function formatExcelImportMessage(load) {
	const summary = (load && load.summary) || {};
	const rows = (load && load.rows) || [];

	const lines = [];
	lines.push('Excel rows : ' + (summary.excel_row_count || 0));
	lines.push('Lookup keys : ' + (summary.lookup_key_count || 0));
	lines.push('Added items : ' + (summary.item_count || 0) + ' (qty ' + (summary.total_quantity || 0) + ')');
	if (summary.header_row_count) lines.push('Header rows skipped : ' + summary.header_row_count);

	appendGroup(lines, 'NOT_FOUND', rows);
	appendGroup(lines, 'AMBIGUOUS', rows);
	appendGroup(lines, 'INELIGIBLE', rows);
	appendGroup(lines, 'INVALID', rows);

	return lines.join('<br>');
}

/** 중복 품목 알림도 개수 + 제한된 예시로만 보여준다. */
export function formatDuplicatedItemsMessage(duplicated_labels) {
	const shown = duplicated_labels.slice(0, MAX_MESSAGE_EXAMPLES).join(', ');
	const rest = duplicated_labels.length - MAX_MESSAGE_EXAMPLES;
	const suffix = rest > 0 ? ' and ' + rest + ' more' : '';
	return duplicated_labels.length + ' item(s) already exist in the Purchase List' + '\n' + shown + suffix + '\nPlease check the quantity of these products.';
}
